/*
  BCo PDP redesign (10-9-26)
  - Gallery: one large photo, thumbnails below, photos filtered to the selected color
    (snippets/product-information-v1-gallery.liquid marks each photo's color), swipe on phones,
    click to zoom.
  - Keeps the Shop Pay Installments ("Pay in 4") form on the selected variant.
  - Description: shows "Read more" only when the description is longer than the preview.
  Listens to the "piv1:variant" event from product-information-v1.js. The old 2-up gallery code in
  product-information-v1.js only runs on [data-piv1-gallery], which the new gallery doesn't use.

  BCo 10-13-26 (dead-tap fixes from Clarity):
  - Tapping the thumbnail already showing opens the zoom view instead of doing nothing.
  - The photos on both sides of the current one load ahead of time (not just the next one), and a
    photo that is still loading shows a spinner, so arrow taps always give visible feedback.
  - Tapping the photo in the zoom view closes it.
*/
(function () {
  'use strict';

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function largestSrc(img) {
    if (!img) return '';
    var best = img.currentSrc || img.src || '';
    var bestW = 0;
    (img.getAttribute('srcset') || '').split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      var w = parseInt(bits[1], 10) || 0;
      if (bits[0] && w > bestW) {
        bestW = w;
        best = bits[0];
      }
    });
    return best;
  }

  /* ---------- Zoom (shared lightbox) ---------- */
  var lightbox = null;
  function getLightbox() {
    if (lightbox) return lightbox;
    var dlg = document.createElement('dialog');
    dlg.className = 'bco-lightbox';
    dlg.setAttribute('aria-label', 'Product photo');
    dlg.innerHTML =
      '<img class="bco-lightbox__img" alt="">' +
      '<button type="button" class="bco-lightbox__btn bco-lightbox__close" aria-label="Close">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      '<button type="button" class="bco-lightbox__btn bco-lightbox__prev" aria-label="Previous photo">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg></button>' +
      '<button type="button" class="bco-lightbox__btn bco-lightbox__next" aria-label="Next photo">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg></button>' +
      '<p class="bco-lightbox__count" aria-live="polite"></p>';
    document.body.appendChild(dlg);
    lightbox = {
      el: dlg,
      img: dlg.querySelector('.bco-lightbox__img'),
      count: dlg.querySelector('.bco-lightbox__count'),
      prev: dlg.querySelector('.bco-lightbox__prev'),
      next: dlg.querySelector('.bco-lightbox__next'),
      owner: null
    };
    dlg.querySelector('.bco-lightbox__close').addEventListener('click', function () {
      dlg.close();
    });
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) dlg.close();
    });
    // BCo 10-13-26: tapping the zoomed photo closes the zoom view (it used to do nothing).
    lightbox.img.addEventListener('click', function () {
      dlg.close();
    });
    lightbox.prev.addEventListener('click', function () {
      if (lightbox.owner) lightbox.owner.step(-1, true);
    });
    lightbox.next.addEventListener('click', function () {
      if (lightbox.owner) lightbox.owner.step(1, true);
    });
    dlg.addEventListener('keydown', function (e) {
      if (!lightbox.owner) return;
      if (e.key === 'ArrowLeft') lightbox.owner.step(-1, true);
      if (e.key === 'ArrowRight') lightbox.owner.step(1, true);
    });
    return lightbox;
  }

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    var gallery = root.querySelector('[data-bco-gallery]');
    if (!gallery || gallery.__bcoReady) return;
    gallery.__bcoReady = true;

    var frames = toArray(gallery.querySelectorAll('[data-bco-frame]'));
    var thumbs = toArray(gallery.querySelectorAll('[data-bco-thumb]'));
    var thumbsWrap = gallery.querySelector('[data-bco-thumbs]');
    var main = gallery.querySelector('[data-bco-main]');
    var prevBtn = gallery.querySelector('[data-bco-prev]');
    var nextBtn = gallery.querySelector('[data-bco-next]');
    var counter = gallery.querySelector('[data-bco-counter]');
    if (!frames.length) return;

    var grouped = gallery.hasAttribute('data-grouped');
    var colorIndex = parseInt(gallery.getAttribute('data-color-index'), 10);
    var color = gallery.getAttribute('data-current-color') || '';
    var list = [];
    var pos = 0;

    // The selected color's own photos come first, then photos shared by every color
    // (lifestyle/group shots placed before the first color photo in the product's media).
    function buildList() {
      var own = [];
      var shared = [];
      frames.forEach(function (f, i) {
        var c = f.getAttribute('data-color') || '';
        if (!grouped) own.push(i);
        else if (!c) shared.push(i);
        else if (c === color) own.push(i);
      });
      list = own.concat(shared);
      if (!list.length) frames.forEach(function (f, i) { list.push(i); });
      thumbs.forEach(function (t) { t.style.order = ''; });
      list.forEach(function (fi, p) {
        if (thumbs[fi]) thumbs[fi].style.order = String(p);
      });
    }

    function warm(i) {
      var f = frames[i];
      var img = f && f.querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }

    // BCo 10-13-26: while the shown photo is still downloading, show a spinner on it so a tap
    // on an arrow or thumbnail never looks like it did nothing.
    function markLoading(frame) {
      var img = frame && frame.querySelector('img');
      if (!img || img.complete) {
        if (frame) frame.classList.remove('is-loading');
        return;
      }
      frame.classList.add('is-loading');
      var done = function () { frame.classList.remove('is-loading'); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }

    function show(p) {
      if (!list.length) return;
      pos = ((p % list.length) + list.length) % list.length;
      var current = list[pos];
      warm(current);
      frames.forEach(function (f, i) {
        f.hidden = i !== current;
      });
      markLoading(frames[current]);
      thumbs.forEach(function (t, i) {
        var inList = list.indexOf(i) !== -1;
        t.hidden = !inList;
        var on = i === current;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
        if (on && thumbsWrap) {
          var left = t.offsetLeft - thumbsWrap.offsetLeft;
          if (left < thumbsWrap.scrollLeft || left + t.offsetWidth > thumbsWrap.scrollLeft + thumbsWrap.clientWidth) {
            thumbsWrap.scrollTo({ left: Math.max(0, left - 8), behavior: 'smooth' });
          }
        }
      });
      if (counter) counter.textContent = (pos + 1) + ' / ' + list.length;
      var single = list.length < 2;
      if (prevBtn) prevBtn.hidden = single;
      if (nextBtn) nextBtn.hidden = single;
      if (counter) counter.hidden = single;
      if (thumbsWrap) thumbsWrap.hidden = single;
      // Load the photos on both sides ahead of time so arrows never show an empty frame.
      // (BCo 10-13-26: was next photo only, so "previous" from photo 1 showed an unloaded photo.)
      if (list.length > 1) {
        warm(list[(pos + 1) % list.length]);
        warm(list[(pos - 1 + list.length) % list.length]);
      }
      if (lightbox && lightbox.owner === api && lightbox.el.open) syncLightbox();
    }

    function step(d) {
      show(pos + d);
    }

    function syncLightbox() {
      var lb = getLightbox();
      var img = frames[list[pos]] && frames[list[pos]].querySelector('img');
      lb.img.src = largestSrc(img);
      lb.img.alt = img ? img.alt : '';
      lb.count.textContent = (pos + 1) + ' / ' + list.length;
      lb.prev.hidden = lb.next.hidden = list.length < 2;
    }

    var api = { step: step };

    function openZoom() {
      var lb = getLightbox();
      lb.owner = api;
      syncLightbox();
      if (typeof lb.el.showModal === 'function') lb.el.showModal();
      else lb.el.setAttribute('open', '');
    }

    // Start on the photo the server marked visible.
    buildList();
    var startFrame = -1;
    frames.forEach(function (f, i) {
      if (!f.hidden && startFrame === -1) startFrame = i;
    });
    var startPos = list.indexOf(startFrame);
    show(startPos === -1 ? 0 : startPos);

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () {
        var p = list.indexOf(i);
        if (p === -1) return;
        // BCo 10-13-26: the thumbnail already showing opens the zoom view (it used to do nothing).
        if (p === pos) openZoom();
        else show(p);
      });
    });
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });
    toArray(gallery.querySelectorAll('[data-bco-zoom]')).forEach(function (b) {
      b.addEventListener('click', openZoom);
    });

    // Swipe on touch screens.
    if (main) {
      var x0 = null;
      var y0 = null;
      main.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      }, { passive: true });
      main.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    // Color change: show that color's photos, starting with the variant's own photo.
    root.addEventListener('piv1:variant', function (ev) {
      var v = ev.detail;
      if (!v) return;
      var newColor = color;
      if (grouped && colorIndex >= 0) {
        newColor = (v.options && v.options[colorIndex]) || v['option' + (colorIndex + 1)] || color;
      }
      var mediaId = v.featured_media && v.featured_media.id ? String(v.featured_media.id) : '';
      if (newColor === color && !mediaId) return;
      var colorChanged = newColor !== color;
      color = newColor;
      gallery.setAttribute('data-current-color', color);
      buildList();
      var target = -1;
      if (mediaId) {
        frames.forEach(function (f, i) {
          if (f.getAttribute('data-media-id') === mediaId) target = i;
        });
      }
      var p = target === -1 ? -1 : list.indexOf(target);
      if (p === -1 && !colorChanged) return; // same color, no photo of its own: stay put
      show(p === -1 ? 0 : p);
    });

    // After the page settles, fetch the first photo of each color so color switches are instant.
    window.addEventListener('load', function () {
      setTimeout(function () {
        var seen = {};
        frames.forEach(function (f, i) {
          var c = f.getAttribute('data-color') || '';
          if (c && !seen[c]) {
            seen[c] = true;
            warm(i);
          }
        });
      }, 1200);
    });
  }

  /* ---------- Pay in 4 form follows the selected variant ---------- */
  function initTerms(root) {
    var form = root.querySelector('[data-bco-terms-form]');
    if (!form) return;
    var input = form.querySelector('input[name="id"]');
    if (!input) return;
    root.addEventListener('piv1:variant', function (ev) {
      var v = ev.detail;
      if (!v || String(v.id) === input.value) return;
      input.value = String(v.id);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Description preview ---------- */
  function initDescription(root) {
    toArray(root.querySelectorAll('[data-bco-desc]')).forEach(function (wrap) {
      var body = wrap.querySelector('[data-bco-desc-body]');
      var btn = wrap.querySelector('[data-bco-desc-more]');
      if (!body || !btn) return;
      function measure() {
        if (wrap.classList.contains('is-open')) return;
        btn.hidden = body.scrollHeight <= body.clientHeight + 2;
      }
      btn.addEventListener('click', function () {
        var open = !wrap.classList.contains('is-open');
        wrap.classList.toggle('is-open', open);
        btn.textContent = open ? 'Show less' : 'Read more';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      measure();
      window.addEventListener('load', measure);
      window.addEventListener('resize', measure);
    });
  }

  function boot() {
    toArray(document.querySelectorAll('[data-piv1]')).forEach(function (root) {
      initGallery(root);
      initTerms(root);
      initDescription(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
